/** AP-46 — Baustellendokumente (`/site-docs`). Die Arten haben eigene Pfade (`/site-docs/<art>`, BD-30). */
import {
  SITE_DOC_KIND_PATH,
  type MontageAbnahmeKandidat,
  type MontageMangelZeile,
  type RegieGeraeteVorrat,
  type RegieVorbelegung,
  type DashboardAntwort,
  type SiteDocDetail,
  type SiteDocKind,
  type SiteDocKindSettings,
  type SiteDocListRow,
  type SiteDocPreview,
  type SiteDocProjectChoice,
  type SiteDocProjectSource,
  type SiteDocRelease,
  type SiteDocSignerRole,
  type SiteDocAiProposal,
  type SiteDocLinkRecipient,
  type SiteDocAiProposalInput,
  type SiteDocAiSettings,
  type SiteDocPublicCheck,
  type SiteDocPublicConfirm,
  type SiteDocCountersignRecipient,
  type SiteDocCountersignRequestView,
  type SiteDocPublicCountersign,
  type TagebuchBehinderungArt,
  type TagebuchBehinderungsKette,
  type TagebuchGeraeteVorrat,
  type TagebuchKalender,
  type TagebuchVorbelegung,
} from '@symphenia/shared-types'
import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'

export const siteDocUrl = (id: string) => `/site-docs/${id}`
const artUrl = (kind: SiteDocKind, id?: string) => `/site-docs/${SITE_DOC_KIND_PATH[kind]}${id ? `/${id}` : ''}`

// ── Akte ────────────────────────────────────────────────────────────────────

export interface ListSiteDocsParams {
  kind?: string
  status?: string
  projectId?: string
  projectRefId?: string
  projectSource?: SiteDocProjectSource
  createdById?: string
  from?: string
  to?: string
  copyOpen?: boolean
  filesMissing?: boolean
  onDevice?: boolean
  awaiting?: boolean
  notFetched?: boolean
  /** AP-27 7. — Abrechnungsstand der Regieberichte; `BILLABLE` = abrechnungsreif */
  billing?: 'OPEN' | 'PARTIAL' | 'BILLED' | 'BILLABLE' | 'EXPORTED' | 'NOT_EXPORTED'
  q?: string
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface SiteDocListe {
  data: SiteDocListRow[]
  meta: { total: number; page: number; limit: number; totalPages: number }
  onDevice: SiteDocListRow[]
}

export const fetchSiteDocs = (params: ListSiteDocsParams) => api.get<SiteDocListe>('/site-docs', { params }).then((r) => r.data)

export const siteDocsExportRequest = (params: ListSiteDocsParams & { format: string; orientation?: string }, columns: { key: string; label: string }[]): ExportRequest => ({
  url: '/site-docs/export',
  params: { ...params, columns: JSON.stringify(columns) },
})

/** AP-27 19. — Regiezeilen für ein fremdes ERP (vermerkt „exportiert am"). */
export const regieLinesExportRequest = (params: { from?: string; to?: string; notExported?: boolean; format: string; orientation?: string }): ExportRequest => ({
  url: '/site-docs/regie/lines/export',
  params,
})

export const fetchSiteDocsDashboard = (scope: 'all' | 'mine') => api.get<{ data: DashboardAntwort }>('/site-docs/dashboard', { params: { scope } }).then((r) => r.data.data)

export const fetchSiteDoc = (id: string) => api.get<{ data: SiteDocDetail }>(siteDocUrl(id)).then((r) => r.data.data)

/** PDF der aktuellen Fassung — mit dem Dateinamen des Servers. */
export const fetchSiteDocPdf = (id: string) =>
  api.get<ArrayBuffer>(`${siteDocUrl(id)}/pdf`, { responseType: 'arraybuffer' }).then((r) => ({ daten: r.data, disposition: r.headers['content-disposition'] as string | undefined }))

export const fetchSiteDocFile = (id: string, fileId: string) => api.get<Blob>(`${siteDocUrl(id)}/files/${fileId}`, { responseType: 'blob' }).then((r) => r.data)

/** „Kopie senden" (10.2) — Anfrage für den Maildialog: Download `…/pdf`, Mail `…/mail`. */
export const siteDocCopyRequest = (id: string): ExportRequest => ({ url: `${siteDocUrl(id)}/pdf`, mailUrl: `${siteDocUrl(id)}/mail` })

// ── Anlegen und Kopf (je Art) ───────────────────────────────────────────────

export interface CreateSiteDocInput {
  project: { source: SiteDocProjectSource; projectId?: string | null; projectRefId?: string | null; text?: { number?: string | null; name?: string | null; customer?: string | null; site?: string | null } | null }
  documentDate: string
  title: string
  content?: Record<string, unknown> | null
}

export const createSiteDoc = (kind: SiteDocKind, input: CreateSiteDocInput) => api.post<{ data: SiteDocDetail }>(artUrl(kind), input).then((r) => r.data.data)

export const discardSiteDoc = (kind: SiteDocKind, id: string, reason?: string) => api.delete(artUrl(kind, id), { data: reason ? { reason } : {} }).then((r) => r.data)

export const fetchProjectChoices = (q?: string) =>
  api.get<{ data: SiteDocProjectChoice[]; sources: SiteDocProjectSource[] }>('/site-docs/projects', { params: q ? { q } : {} }).then((r) => r.data)

// ── Freigabe (9.) ───────────────────────────────────────────────────────────

/** „Zur Unterschrift vorlegen" (9. Schritt 2): vergibt die Nummer, der Entwurf bleibt änderbar. */
export const presentSiteDoc = (kind: SiteDocKind, id: string) => api.post(`${artUrl(kind, id)}/present`).then((r) => r.data)

/** Der Stand, wie er eingefroren wird, mit den Kontakten für den Unterzeichner. */
export const fetchSiteDocPreview = (id: string, release: SiteDocRelease) =>
  api.get<{ data: SiteDocPreview }>(`${siteDocUrl(id)}/preview`, { params: { release } }).then((r) => r.data.data)

export interface SignatureInput {
  role: SiteDocSignerRole
  name: string
  function: string | null
  email: string | null
  contactId: string | null
  /** `data:image/png;base64,…` */
  image: string
}

export const finalizeSiteDoc = (kind: SiteDocKind, id: string, release: SiteDocRelease, signatures: SignatureInput[], linkRecipient?: SiteDocLinkRecipient) =>
  api.post(`${artUrl(kind, id)}/finalize`, { release, signatures, ...(linkRecipient ? { linkRecipient } : {}) }).then((r) => r.data)

/** AP-46 22.2 — Bestätigung per Link neu senden; das alte Token gilt nicht mehr. */
export const resendSiteDocConfirmation = (id: string, empfaenger: SiteDocLinkRecipient) =>
  api.post<{ data: SiteDocDetail }>(`${siteDocUrl(id)}/confirmation/resend`, empfaenger).then((r) => r.data.data)

export const declineSiteDoc = (kind: SiteDocKind, id: string, reason: string) => api.post(`${artUrl(kind, id)}/decline`, { reason }).then((r) => r.data)

export const countersignSiteDoc = (kind: SiteDocKind, id: string, signature: SignatureInput) => api.post(`${artUrl(kind, id)}/signatures`, signature).then((r) => r.data)

export const supplementSiteDoc = (kind: SiteDocKind, id: string) => api.post<{ data: SiteDocDetail }>(`${artUrl(kind, id)}/supplement`).then((r) => r.data.data)

// ── Kern: Zuordnung, Gerät, Storno, Dateien ─────────────────────────────────

export const assignSiteDoc = (id: string, body: { project?: CreateSiteDocInput['project']; customerId?: string | null; positionId?: string | null; workStepId?: string | null }) =>
  api.patch<{ data: SiteDocDetail }>(`${siteDocUrl(id)}/assignment`, body).then((r) => r.data.data)

export const handSiteDocToDevice = (id: string, userId: string) => api.post(`${siteDocUrl(id)}/hand-to-device`, { userId }).then((r) => r.data)

export const takeBackSiteDoc = (id: string) => api.post(`${siteDocUrl(id)}/take-back`).then((r) => r.data)

export const markSiteDocFileLost = (id: string, fileId: string, reason: string) => api.post(`${siteDocUrl(id)}/files/${fileId}/lost`, { reason }).then((r) => r.data)

export const voidSiteDoc = (id: string, reason: string, withReplacement: boolean) =>
  api.post<{ data: { voided: SiteDocDetail; replacement: SiteDocDetail | null } }>(`${siteDocUrl(id)}/void`, { reason, withReplacement }).then((r) => r.data.data)

export const renewSiteDocToken = (id: string) => api.post(`${siteDocUrl(id)}/public-token/renew`).then((r) => r.data)

/** Mitarbeiter für „An Gerät geben" — die schlanke Liste der Benutzer. */
export const fetchUserChoices = () => api.get<{ data: { id: string; name: string }[] }>('/site-docs/people').then((r) => r.data.data)

// ── Einstellungen (11.2) ────────────────────────────────────────────────────

export const fetchSiteDocSettings = () => api.get<{ data: SiteDocKindSettings[] }>('/site-docs/settings/kinds').then((r) => r.data.data)

export interface NumberBlockRow {
  id: string
  kind: SiteDocKind
  year: number
  firstValue: number
  lastValue: number
  withdrawn: boolean
  userId: string
  userName: string
  deviceId: string
  reservedAt: string
  returnedAt: string | null
  withdrawnAt: string | null
  used: number
}

export const fetchNumberBlocks = () => api.get<{ data: NumberBlockRow[] }>('/site-docs/settings/number-blocks').then((r) => r.data.data)

export const withdrawNumberBlock = (id: string) => api.post(`/site-docs/settings/number-blocks/${id}/withdraw`).then((r) => r.data)

export interface NumberGapRow {
  kind: SiteDocKind
  year: number
  seq: number
  number: string
  reason: 'BLOCK_UNUSED' | 'DISCARDED' | 'ON_DEVICE'
  userId: string | null
  userName: string | null
  deviceId: string | null
  at: string | null
  note: string | null
}

export const fetchNumberGaps = () => api.get<{ data: NumberGapRow[] }>('/site-docs/settings/number-gaps').then((r) => r.data.data)

export interface QuarantineRow {
  id: string
  clientId: string
  userId: string
  userName: string
  deviceId: string | null
  reason: string
  receivedAt: string
  resolvedAt: string | null
  resolvedById: string | null
  note: string | null
}

export const fetchQuarantine = () => api.get<{ data: QuarantineRow[] }>('/site-docs/settings/quarantine').then((r) => r.data.data)

export const fetchQuarantineEntry = (id: string) => api.get<{ data: Record<string, unknown> }>(`/site-docs/settings/quarantine/${id}`).then((r) => r.data.data)

export const resolveQuarantine = (id: string, note?: string) => api.post(`/site-docs/settings/quarantine/${id}/resolve`, { note }).then((r) => r.data)

/** Ein Blob als Data-URL — so nimmt der Server Unterschriftsbilder im ERP an. */
export function alsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const leser = new FileReader()
    leser.onload = () => resolve(String(leser.result))
    leser.onerror = () => reject(leser.error)
    leser.readAsDataURL(blob)
  })
}

/** Kopf eines ERP-Entwurfs feldweise ändern (AP-36, mit Stand). */
export const updateSiteDoc = (kind: SiteDocKind, id: string, patch: Record<string, unknown>, config?: { headers?: Record<string, string> }) =>
  api.patch<{ data: SiteDocDetail }>(artUrl(kind, id), patch, config).then((r) => r.data.data)

// ── Fotos im ERP-Entwurf (je Art) ───────────────────────────────────────────

export const addSiteDocPhoto = (kind: SiteDocKind, id: string, file: File, caption?: string) => {
  const form = new FormData()
  form.append('file', file, file.name)
  if (caption) form.append('caption', caption)
  return api.post<{ data: SiteDocDetail }>(`${artUrl(kind, id)}/photos`, form).then((r) => r.data.data)
}

export const captionSiteDocPhoto = (kind: SiteDocKind, id: string, fileId: string, caption: string | null) =>
  api.patch<{ data: SiteDocDetail }>(`${artUrl(kind, id)}/photos/${fileId}`, { caption }).then((r) => r.data.data)

export const removeSiteDocPhoto = (kind: SiteDocKind, id: string, fileId: string) => api.delete<{ data: SiteDocDetail }>(`${artUrl(kind, id)}/photos/${fileId}`).then((r) => r.data.data)

// ── Regiebericht (AP-27 10.1) ───────────────────────────────────────────────

export const regieLineCreate = (id: string, body: Record<string, unknown>) => api.post<{ data: SiteDocDetail }>(`/site-docs/regie/${id}/lines`, body).then((r) => r.data.data)

export const regieLineUpdate = (id: string, lineId: string, body: Record<string, unknown>) => api.patch<{ data: SiteDocDetail }>(`/site-docs/regie/${id}/lines/${lineId}`, body).then((r) => r.data.data)

export const regieLineDelete = (id: string, lineId: string) => api.delete<{ data: SiteDocDetail }>(`/site-docs/regie/${id}/lines/${lineId}`).then((r) => r.data.data)

export const regieLineAssign = (id: string, lineId: string, body: Record<string, unknown>) =>
  api.patch<{ data: SiteDocDetail }>(`/site-docs/regie/${id}/lines/${lineId}/assignment`, body).then((r) => r.data.data)

/** AP-27 7. — offene Zeilen unterschriebener Berichte in die Ablage legen (nur mit Belegwesen). */
export const regieInDieAblage = (body: { documentIds?: string[]; lineIds?: string[] }) =>
  api.post<{ data: { lines: number; reports: number } }>('/site-docs/regie/clipboard', body).then((r) => r.data.data)

export const regiePrefill = (q: { projectId?: string | null; projectRefId?: string | null; date: string }) =>
  api.get<{ data: RegieVorbelegung[] }>('/site-docs/regie/prefill', { params: { projectId: q.projectId ?? undefined, projectRefId: q.projectRefId ?? undefined, date: q.date } }).then((r) => r.data.data)

export const regieDeviceCache = (q: { projectId?: string | null; projectRefId?: string | null }) =>
  api.get<{ data: RegieGeraeteVorrat }>('/site-docs/regie/device-cache', { params: { projectId: q.projectId ?? undefined, projectRefId: q.projectRefId ?? undefined } }).then((r) => r.data.data)

// ── Bautagebuch (AP-48 11.) ─────────────────────────────────────────────────

/** Das Projekt eines Bautagesberichts: Projektstamm, Zeiterfassung oder frei eingetragen (AP-46 24.). */
export interface TagebuchBezug {
  projectId?: string | null
  projectRefId?: string | null
  projectLabel?: string | null
}

/** Nur ein Bezug geht hinaus — der Server sucht über den ersten, den er findet. */
const bezugParams = (b: TagebuchBezug) =>
  b.projectId ? { projectId: b.projectId } : b.projectRefId ? { projectRefId: b.projectRefId } : b.projectLabel?.trim() ? { projectLabel: b.projectLabel.trim() } : {}

/** Bezug eines Dokuments — frei eingetragene Projekte über ihren Text. */
export const bezugVon = (d: { projectId: string | null; projectRefId: string | null; projectSource: SiteDocProjectSource; projectLabel: string }): TagebuchBezug =>
  d.projectId ? { projectId: d.projectId } : d.projectRefId ? { projectRefId: d.projectRefId } : d.projectSource === 'TEXT' ? { projectLabel: d.projectLabel } : {}

/** AP-48 4.2–4.4 — Vorbelegung: Belegschaft aus den Buchungen, Lieferungen, „wie im letzten Bericht". */
export const tagebuchPrefill = (q: TagebuchBezug & { date: string }) =>
  api.get<{ data: TagebuchVorbelegung }>('/site-docs/diary/prefill', { params: { ...bezugParams(q), date: q.date } }).then((r) => r.data.data)

/** Gerätevorrat — hier gebraucht für `namenZeigen` und die Geräte aus dem Artikelstamm. */
export const tagebuchDeviceCache = () => api.get<{ data: TagebuchGeraeteVorrat }>('/site-docs/diary/device-cache').then((r) => r.data.data)

/** AP-48 7. — Monatskalender eines Projekts (`month` = `JJJJ-MM`). */
export const tagebuchKalender = (q: TagebuchBezug & { month: string }) =>
  api.get<{ data: TagebuchKalender }>('/site-docs/diary/calendar', { params: { ...bezugParams(q), month: q.month } }).then((r) => r.data.data)

/** AP-48 7. (TB-13) — Bautagebuch als PDF: Download `book/pdf`, Mail `book/mail` mit denselben Parametern. */
export const tagebuchBuchRequest = (q: TagebuchBezug & { from: string; to: string }): ExportRequest => ({
  url: '/site-docs/diary/book/pdf',
  mailUrl: '/site-docs/diary/book/mail',
  params: { ...bezugParams(q), from: q.from, to: q.to },
})

export interface BehinderungenParams extends TagebuchBezug {
  from?: string
  to?: string
  art?: TagebuchBehinderungArt
  ongoing?: boolean
  costExpected?: boolean
  q?: string
  sortBy?: string
  sortDir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

const behinderungenQuery = ({ projectId, projectRefId, projectLabel, ...rest }: BehinderungenParams) => ({ ...bezugParams({ projectId, projectRefId, projectLabel }), ...rest })

export interface BehinderungenListe {
  data: TagebuchBehinderungsKette[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

/** AP-48 4.4, 7. — Behinderungsliste: je Kette eine Zeile. */
export const fetchBehinderungen = (params: BehinderungenParams) => api.get<BehinderungenListe>('/site-docs/diary/hindrances', { params: behinderungenQuery(params) }).then((r) => r.data)

export const behinderungenExportRequest = (params: BehinderungenParams & { format: string; orientation?: string }, columns: { key: string; label: string }[]): ExportRequest => ({
  url: '/site-docs/diary/hindrances/export',
  params: { ...behinderungenQuery(params), columns: JSON.stringify(columns) },
})

// ── Sammel-Gegenzeichnung per Link (AP-48 RN-6) ─────────────────────────────

export const fetchCountersignRequests = (q: { documentId?: string; projectId?: string; projectRefId?: string }) =>
  api.get<{ data: SiteDocCountersignRequestView[] }>('/site-docs/countersign-requests', { params: q }).then((r) => r.data.data)

export const createCountersignRequest = (body: { documentIds: string[]; recipient: SiteDocCountersignRecipient }) =>
  api.post<{ data: SiteDocCountersignRequestView }>('/site-docs/countersign-requests', body).then((r) => r.data.data)

export const revokeCountersignRequest = (id: string) => api.post<{ data: SiteDocCountersignRequestView }>(`/site-docs/countersign-requests/${id}/revoke`).then((r) => r.data.data)

export const resendCountersignRequest = (id: string) => api.post<{ data: SiteDocCountersignRequestView }>(`/site-docs/countersign-requests/${id}/resend`).then((r) => r.data.data)

// ── AP-46 21. — Diktat mit KI ──────────────────────────────────────────────

export const fetchSiteDocAiSettings = () => api.get<{ data: SiteDocAiSettings }>('/site-docs/settings/ai').then((r) => r.data.data)

export const setSiteDocAiSettings = (body: { enabled: boolean; accepted?: boolean }) => api.put<{ data: SiteDocAiSettings }>('/site-docs/settings/ai', body).then((r) => r.data.data)

/** Vorschlag aus dem Diktat — gespeichert wird nichts. */
export const siteDocAiProposal = (body: SiteDocAiProposalInput) => api.post<{ data: SiteDocAiProposal }>('/site-docs/ai/proposal', body).then((r) => r.data.data)

// ── AP-46 22./23. — öffentliche Seiten (ohne Anmeldung) ────────────────────

const oeffentlich = (pfad: string) => `${api.defaults.baseURL ?? ''}/site-docs/public/${pfad}`

export const publicConfirmView = (token: string) => api.get<{ data: SiteDocPublicConfirm }>(`/site-docs/public/confirm/${encodeURIComponent(token)}`).then((r) => r.data.data)

export const publicConfirmSign = (token: string, body: { name: string; function: string | null; image: string }) =>
  api.post<{ data: SiteDocPublicConfirm }>(`/site-docs/public/confirm/${encodeURIComponent(token)}`, body).then((r) => r.data.data)

export const publicConfirmDecline = (token: string, body: { reason: string; name: string | null }) =>
  api.post<{ data: SiteDocPublicConfirm }>(`/site-docs/public/confirm/${encodeURIComponent(token)}/decline`, body).then((r) => r.data.data)

export const publicConfirmFotoUrl = (token: string, fileId: string) => oeffentlich(`confirm/${encodeURIComponent(token)}/photos/${fileId}`)

export const publicCheck = (token: string) => api.get<{ data: SiteDocPublicCheck }>(`/site-docs/public/check/${encodeURIComponent(token)}`).then((r) => r.data.data)

export const publicCheckPdfUrl = (token: string) => oeffentlich(`check/${encodeURIComponent(token)}/pdf`)

// ── AP-47 Montageprotokoll ──────────────────────────────────────────────────

export interface ListDefectsParams {
  projectId?: string
  projectRefId?: string
  documentId?: string
  state?: string
  weight?: 'WESENTLICH' | 'UNWESENTLICH'
  overdue?: boolean
  from?: string
  to?: string
  q?: string
  sort?: string
  dir?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface DefectListe {
  data: MontageMangelZeile[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

export const fetchAssemblyDefects = (params: ListDefectsParams) => api.get<DefectListe>('/site-docs/assembly/defects', { params }).then((r) => r.data)

export const assemblyDefectsExportRequest = (params: ListDefectsParams & { format: string; orientation?: string }, columns: { key: string; label: string }[]): ExportRequest => ({
  url: '/site-docs/assembly/defects/export',
  params: { ...params, columns: JSON.stringify(columns) },
})

export const resolveAssemblyDefect = (id: string, note: string) => api.post<{ data: MontageMangelZeile }>(`/site-docs/assembly/defects/${id}/resolve`, { note }).then((r) => r.data.data)

export const reopenAssemblyDefect = (id: string, note?: string) => api.post<{ data: MontageMangelZeile }>(`/site-docs/assembly/defects/${id}/reopen`, { note }).then((r) => r.data.data)

export const fetchAssemblyCandidates = (q: { projectId?: string | null; projectRefId?: string | null; projectLabel?: string | null }) =>
  api.get<{ data: MontageAbnahmeKandidat[] }>('/site-docs/assembly/acceptance-candidates', { params: { projectId: q.projectId ?? undefined, projectRefId: q.projectRefId ?? undefined, projectLabel: q.projectLabel || undefined } }).then((r) => r.data.data)

export const fetchAssemblyPrefill = (q: { projectId?: string | null; projectRefId?: string | null; date: string }) =>
  api.get<{ data: string[] }>('/site-docs/assembly/prefill', { params: { projectId: q.projectId ?? undefined, projectRefId: q.projectRefId ?? undefined, date: q.date } }).then((r) => r.data.data)

export interface MontagePosition {
  id: string
  number: number
  name: string
  executionClass: string | null
  steps: { id: string; number: number; name: string; isAssembly: boolean; status: string }[]
}

export const fetchAssemblyProjectParts = (projectId: string) => api.get<{ data: MontagePosition[] }>('/site-docs/assembly/project-parts', { params: { projectId } }).then((r) => r.data.data)

export const fetchAssemblySettings = () => api.get<{ data: { extra: Record<string, unknown> | null } }>('/site-docs/assembly/settings').then((r) => r.data.data)

export const linkAssemblyPart = (id: string, partKey: string, body: { positionId: string | null; workStepId: string | null }) =>
  api.put<{ data: SiteDocDetail }>(`/site-docs/assembly/${id}/parts/${encodeURIComponent(partKey)}/link`, body).then((r) => r.data.data)
/** AP-48 RN-6 — Sammel-Gegenzeichnung: Seite, Aufklappen, Zeichnen, Einwand, Fotos. */
const gegen = (token: string) => `/site-docs/public/countersign/${encodeURIComponent(token)}`

export const publicCountersignView = (token: string) => api.get<{ data: SiteDocPublicCountersign }>(gegen(token)).then((r) => r.data.data)

export const publicCountersignOpen = (token: string, itemId: string) => api.post<{ data: SiteDocPublicCountersign }>(`${gegen(token)}/open`, { itemId }).then((r) => r.data.data)

export const publicCountersignSign = (token: string, body: { itemIds: string[]; name: string; function: string | null; image: string }) =>
  api.post<{ data: SiteDocPublicCountersign }>(gegen(token), body).then((r) => r.data.data)

export const publicCountersignObject = (token: string, body: { itemId: string; text: string; name: string | null }) =>
  api.post<{ data: SiteDocPublicCountersign }>(`${gegen(token)}/object`, body).then((r) => r.data.data)

export const publicCountersignFotoUrl = (token: string, fileId: string) => oeffentlich(`countersign/${encodeURIComponent(token)}/photos/${fileId}`)
